import React from "react";
import { useLocation } from "react-router-dom";
import { PRIMARY, HELP, PURPLE, SUCCESS } from "../../utils/constants/colors";
import "./onboarding.css";

// Card accent colors come from the design-system color scales (utils/constants/colors.js)
// instead of hardcoded hex literals.
const FEATURE_CARDS = [
  {
    num: "01",
    accentColor: PRIMARY.W_500,
    iconBg: PRIMARY.W_50,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke={PRIMARY.W_500} strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
        <circle cx="11" cy="11" r="7" />
        <path d="M16 16l5 5" />
      </svg>
    ),
    title: "Find Prospects",
    desc: "Discover and filter the right contacts using advanced criteria: industry, company size, job title, location, and more.",
    tagBg: PRIMARY.W_50,
    tagColor: PRIMARY.W_700,
    tag: "Prospect Discovery",
  },
  {
    num: "02",
    accentColor: HELP.W_500,
    iconBg: HELP.W_50,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke={HELP.W_500} strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
        <rect x="3" y="3" width="7" height="18" />
        <rect x="14" y="9" width="7" height="12" />
        <line x1="14" y1="3" x2="21" y2="3" />
      </svg>
    ),
    title: "Build Marketing Lists",
    desc: "Add selected prospects to targeted lists and organize your audience for outreach campaigns and audience management.",
    tagBg: HELP.W_50,
    tagColor: HELP.W_700,
    tag: "Audience Management",
  },
  {
    num: "03",
    accentColor: PURPLE.W_600,
    iconBg: PURPLE.W_50,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke={PURPLE.W_600} strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
        <path d="M22 2L11 13" />
        <path d="M22 2L15 22 11 13 2 9l20-7z" />
      </svg>
    ),
    title: "Launch Campaigns",
    desc: "Engage prospects through AI-powered sequences across email, calls, and LinkedIn, fully automated and tracked in real time.",
    tagBg: PURPLE.W_50,
    tagColor: PURPLE.W_800,
    tag: "Outreach Automation",
  },
  {
    num: "04",
    accentColor: SUCCESS.W_500,
    iconBg: SUCCESS.W_50,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke={SUCCESS.W_500} strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
      </svg>
    ),
    title: "Manage Relationships",
    desc: "Track contacts, log sales activities, manage opportunities, and monitor your full pipeline through the CRM module.",
    tagBg: SUCCESS.W_50,
    tagColor: SUCCESS.W_800,
    tag: "CRM & Pipeline",
  },
];

const WelcomeScreen = () => {
  const location  = useLocation();
  const userData  = location.state || {};
  const subdomain = userData.subdomain || "";
  const firstName = userData.firstName || "";

  const workspaceUrl = subdomain ? `${subdomain}.reachiq.ai` : "your-workspace.reachiq.ai";
  const tenantUrl    = subdomain ? `https://${subdomain}.reachiq.ai` : "#";

  // Both CTAs enter the same workspace (the only destination), but they carry
  // distinct intent so they are no longer wired to one identical handler:
  //   • primary   → guided product tour
  //   • secondary → skip the tour and explore unaided
  const enterWorkspace = (tour) => {
    if (tenantUrl === "#") return; // no subdomain in state — nothing to navigate to
    // Encode the intent so the tenant app can branch on it: guided tour vs skip.
    const url = `${tenantUrl}?tour=${tour === "skip" ? "skip" : "guided"}`;
    window.location.href = url;
  };

  return (
    <div className="onb-wl-outer">

      {/* ── Dark hero ── */}
      <div className="onb-wl-hero">
        <div className="onb-wl-hero-in">
          <div className="onb-wl-ready">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true" focusable="false">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Workspace Ready
          </div>

          <h1 className="onb-wl-h1">
            Welcome to ReachIQ{firstName ? `, ${firstName}` : ""}!
          </h1>

          <p className="onb-wl-sub">
            Your workspace is ready. Here&rsquo;s everything you can do from day one:
            discover prospects, organize outreach, launch campaigns, and manage
            relationships, all from one place.
          </p>
        </div>
      </div>

      {/* ── White body ── */}
      <div className="onb-wl-body">
        <div className="onb-wl-eyebrow">What you can do with ReachIQ</div>

        <div className="onb-wl-cards">
          {FEATURE_CARDS.map((card) => (
            <div className="onb-wl-card" key={card.num}>
              <div className="onb-wl-acc" style={{ background: card.accentColor }} />
              <div className="onb-wl-cb">
                <span className="onb-wl-num">{card.num}</span>
                <div className="onb-wl-ic" style={{ background: card.iconBg }}>
                  {card.icon}
                </div>
                <div className="onb-wl-ct">{card.title}</div>
                <div className="onb-wl-cd">{card.desc}</div>
                <span
                  className="onb-wl-tag"
                  style={{ background: card.tagBg, color: card.tagColor }}
                >
                  {card.tag}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* CTAs — bespoke styled buttons (hero/landing treatment); token-driven via CSS */}
        <div className="onb-wl-cta-wrap">
          <button type="button" className="onb-wl-cta-btn" onClick={() => enterWorkspace("tour")}>
            Start Exploring ReachIQ
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" focusable="false">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>

          <button type="button" className="onb-wl-cta-sec" onClick={() => enterWorkspace("skip")}>
            I'll Explore Myself
          </button>
        </div>

        <div className="onb-wl-ws-line">
          Your workspace: <strong>{workspaceUrl}</strong>
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;
